<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, ref, watch, onMounted } from 'vue'
import { useLogger } from 'vue-logger-plugin'
import { TextBox } from '@progress/kendo-vue-inputs'
import { DropDownList } from '@progress/kendo-vue-dropdowns'
import { GetBeeNumberAuthenicatedUser } from '../../stores/SettingsStore.ts'
import { TaxExemptNUWCreate, PwsTaxExemptGet } from '../../api/Business.ts'
import type { IWizardStepProps, IVatInformation } from '../../types/WizardTypes'

const { t, locale } = useI18n()
const logger = useLogger()

const props = defineProps<IWizardStepProps>()

// Emit events to parent component
const emit = defineEmits<{
  'update:isValid': [isValid: boolean],
  'skip-to-step': [step: number]
}>()

// State for handling loading and errors
const isLoading = ref(false)
const loadError = ref('')
const submitError = ref('')

// Local reactive state for VAT information
const localVatType = ref(props.formData.vatInformation?.vatType || 'VAT')
const localVatNumber = ref(props.formData.vatInformation?.beeNumber || '')
// We don't need taxExemptCode as a separate variable since we're using localVatNumber as the VAT number

// Update parent component whenever local state changes
watch([localVatType, localVatNumber], () => {
  // Extract just the value if it's an object
  const vatTypeValue = typeof localVatType.value === 'object' && localVatType.value !== null
    ? (localVatType.value as any).value
    : localVatType.value

  // Clear VAT number when TAXID is selected
  if (vatTypeValue === 'TAXID' && localVatNumber.value) {
    localVatNumber.value = ''
  }

  props.onUpdate({
    vatInformation: {
      vatType: vatTypeValue, // Ensure we only send the value, not the object
      beeNumber: localVatNumber.value,
      taxExemptCode: localVatNumber.value // Use the VAT number as the taxExemptCode
    }
  })

  // Re-evaluate validation when values change
  validateAndEmit()
})

// VAT type options
const vatTypeOptions = [
  { value: 'VAT', labelKey: 'labels.options.VAT', text: t('labels.options.VAT') },
  { value: 'TAXID', labelKey: 'labels.options.TAXID', text: t('labels.options.TAXID') }
]

// Get the current VAT type value (handle both object and string)
const currentVatType = computed(() => {
  return typeof localVatType.value === 'object' && localVatType.value !== null
    ? (localVatType.value as any).value
    : localVatType.value
})

// Computed property for VAT number placeholder based on locale
const vatNumberPlaceholder = computed(() => {
  const currentLocale = locale.value.toLowerCase()

  // Check if locale is de-DE or de-AT
  if (currentLocale === 'de-de') {
    return t('labels.VatNumberPlaceholderDE')
  } else if (currentLocale === 'de-at') {
    return t('labels.VatNumberPlaceholderAT')
  }

  // Default placeholder for other locales
  return t('labels.VatNumberPlaceholderATDE')
})

// Determine if VAT number field should be disabled
const isVatNumberDisabled = computed(() => {
  return currentVatType.value === 'TAXID'
})

// Validation for VAT number (German: DE + 9 digits, Austrian: AT + 8 digits)
const isValidVatNumber = computed(() => {
  const vatNumber = localVatNumber.value.trim()
  if (!vatNumber) {
    return false
  }

  // German VAT format: DE followed by 9 digits
  const germanVatRegex = /^DE\d{9}$/
  // Austrian VAT format: AT followed by 8 digits (U + 7 digits)
  const austrianVatRegex = /^ATU\d{8}$/

  return germanVatRegex.test(vatNumber) || austrianVatRegex.test(vatNumber)
})

// Determine if the step is valid
const isStepValid = computed(() => {
  // If TAXID is selected, no validation needed - always valid
  if (currentVatType.value === 'TAXID') {
    return true
  }

  // If VAT is selected, validate the VAT number format
  return isValidVatNumber.value
})

// Function to validate and emit the validation state
function validateAndEmit() {
  const isValid = isStepValid.value

  // Log validation state for debugging
  logger.debug('VAT Information validation state:', {
    vatNumber: localVatNumber.value,
    isValidVatNumber: isValidVatNumber.value,
    isValid
  })

  // Emit the validation state to the parent component
  emit('update:isValid', isValid)

  return isValid
}

// Initialize the form with data from parent if available
onMounted(async () => {
  logger.debug('VatInformationStep mounted', {
    vatType: localVatType.value,
    beeNumber: await GetBeeNumberAuthenicatedUser()
  })
  logger.debug('Initial validation state:', isStepValid.value)

  // Don't make any API calls on mount to prevent automatic redirection
  // Just initialize the component with the current values

  // Set loading state to false
  isLoading.value = false

  // Validate the current state without making API calls
  validateAndEmit()
})

// Submit VAT information data
async function submitVatInformation() {
  // Validate before submission
  if (!validateAndEmit()) {
    logger.debug('Cannot submit VAT information - validation failed')
    return false
  }

  // Extract just the value if it's an object
  const vatTypeValue = typeof localVatType.value === 'object' && localVatType.value !== null
    ? (localVatType.value as any).value
    : localVatType.value

  // If TAXID is selected, skip API call and return success immediately
  if (vatTypeValue === 'TAXID') {
    logger.debug('TAXID selected - skipping API call')
    return true
  }

  // Only call API if VAT is selected
  isLoading.value = true
  submitError.value = ''

  try {
    // Get authenticated user's BEE number
    const beeNumber = await GetBeeNumberAuthenicatedUser()

    if (!beeNumber) {
      throw new Error(t('errors.userNotAuthenticatedOrInvalidBeeNumber'))
    }

    // Prepare the request data
    const requestData = {
      BeeNumber: beeNumber.toString(),
      TaxExemptCode: localVatNumber.value, // Use the VAT number input directly
      CodeIdExtendedTaxId: vatTypeValue // Ensure we only send the value, not the object
    }

    logger.debug('Submitting VAT information:', requestData)

    // Call the API to save the VAT information
    await TaxExemptNUWCreate(requestData)

    logger.debug('VAT information saved successfully')
    isLoading.value = false
    return true
  } catch (error) {
    logger.error('Error saving VAT information:', error)
    submitError.value = t('errors.savingVatInformation')
    isLoading.value = false
    return false
  }
}

// Export the submit function to be called from the parent component
defineExpose({
  submitVatInformation
})
</script>

<template>
  <div class="wizard-step">
    <div class="wizard-step-header">
      <h3>{{ t('labels.VatInformationHeader') }}</h3>
      <p>{{ t('labels.VatInformationSubHeader') }}</p>
    </div>

    <div class="form-section">
      <div v-if="isLoading" class="loading-indicator">
        {{ t('labels.LoadingVatInformation') }}
      </div>

      <div v-else-if="loadError" class="alert-message">
        {{ loadError }}
      </div>

      <div v-else class="form-container">
        <!-- VAT Type -->
        <div class="form-row">
          <div class="form-field-container">
            <label class="form-label">{{ t('labels.VatType') }} *</label>
            <DropDownList
              :data-items="vatTypeOptions"
              v-model="localVatType"
              :text-field="'text'"
              :value-field="'value'"
              class="k-dropdown"
            />
          </div>
        </div>

        <!-- VAT Number -->
        <div class="form-row">
          <div class="form-field-container">
            <label class="form-label">
              {{ t('labels.VatNumber') }}
              <span v-if="!isVatNumberDisabled"> *</span>
            </label>
            <TextBox
              v-model="localVatNumber"
              class="k-textbox"
              :placeholder="vatNumberPlaceholder"
              :disabled="isVatNumberDisabled"
              :class="{ 'invalid-input': localVatNumber !== '' && !isValidVatNumber && !isVatNumberDisabled }"
            />
            <div v-if="localVatNumber !== '' && !isValidVatNumber && !isVatNumberDisabled" class="validation-message">
              {{ t('validators.vatNumberFormatATDE') }}
            </div>
            <div v-if="isVatNumberDisabled" class="help-text">
              {{ t('labels.TaxIdNoInputRequired') }}
            </div>
          </div>
        </div>

        <!-- Submit Error Message -->
        <div v-if="submitError" class="alert-message">
          {{ submitError }}
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.wizard-step {
  margin-bottom: 25px;
}

.wizard-step-header {
  margin-bottom: 16px;
}

.wizard-step-header h3 {
  @include typography-h4;
  @include font-weight-semibold;
  margin-bottom: 6px;
}

.wizard-step-header p {
  @include typography-body;
  color: #666;
  margin-bottom: 10px;
}

.form-section {
  margin-bottom: 25px;
  display: flex;
  justify-content: center;
}

.form-container {
  max-width: 600px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.form-row {
  margin-bottom: 16px;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  width: 100%;
}

.form-field-container {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.form-label {
  @include typography-body;
  @include font-weight-medium;
  display: block;
  margin-bottom: 6px;
}

.help-text {
  @include typography-small;
  color: #666;
  margin-top: 4px;
}

.alert-message {
  margin-top: 12px;
  padding: $spacing-2 $spacing-3;
  color: #e53935;
  background-color: rgba(#e53935, 0.1);
  @include typography-small;
  border-radius: $radius-sm;
  text-align: center;
  width: 100%;
}

.loading-indicator {
  margin: $spacing-4 0;
  font-style: italic;
  @include typography-body;
  color: #666;
  text-align: center;
  width: 100%;
}

/* Ensure Kendo UI components have consistent width */
:deep(.k-dropdown),
:deep(.k-textbox) {
  @include typography-body;
  width: 100%;
}

/* Style for invalid input without showing error message */
.invalid-input {
  border-color: #e53935;
}

:deep(.k-textbox.invalid-input) {
  border-color: #e53935;
}

.validation-message {
  @include typography-small;
  color: #e53935;
  margin-top: 4px;
}
</style>
